const CHUNK_RELOAD_KEY = 'chunk-reload-attempted-at'

/**
 * How long a reload attempt blocks the next one. The entry chunk still boots
 * when a lazy route chunk is missing, so a guard cleared on boot would loop
 * forever. A time window instead lets a later deploy in the same session get
 * its own reload while keeping a permanently broken chunk to a single retry.
 */
const RELOAD_COOLDOWN_MS = 30_000

const DYNAMIC_IMPORT_ERROR_PATTERN =
  /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module|failed to load module script/i

/**
 * A deploy replaces the hashed asset filenames, so tabs left open before it
 * request a chunk that no longer exists. The retry wrapper around the lazy
 * routes only re-requests the same dead URL, so the error surfaces on the
 * router errorElement.
 */
function isDynamicImportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return DYNAMIC_IMPORT_ERROR_PATTERN.test(message)
}

function hasReloadedRecently(): boolean {
  const attemptedAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY))
  if (!attemptedAt) return false
  return Date.now() - attemptedAt < RELOAD_COOLDOWN_MS
}

/**
 * Reloads to pick up the new index.html and its chunks. Returns whether the
 * reload was triggered, letting the caller skip reporting an error that is
 * expected right after a deploy.
 */
function reloadOnceForStaleChunk(error: unknown): boolean {
  if (!isDynamicImportError(error)) return false
  if (hasReloadedRecently()) return false

  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
  window.location.reload()
  return true
}

export { isDynamicImportError, reloadOnceForStaleChunk }
